import { useQuery, setLogger } from 'react-query';
import { ComponentProps, useEffect, useMemo, useState } from 'react';
import { noop } from 'lodash';
import {
    LayoutPrimary,
    PrimaryRatio,
    PrimarySize,
    ratiosEqual,
    M_M,
    S_L,
    Optional,
    XL,
    BuyerInsights,
} from '@fiverr-private/homepage_common_types';
import {
    IPrimaryArgs,
    IPrimaryBasicData,
    IPrimaryEnrichDataArgs,
    IPrimaryEnrichment,
    PrimarySDKFunctions,
} from '@fiverr-private/primary_section_sdk';
import { GridItem } from '@fiverr-private/layout_components';
import { logger } from '@fiverr-private/obs';
import { theme } from '@fiverr-private/theme';
import { TailoredHomepageInitialProps } from '../../../lib/interfaces/initialProps';
import { metricsService } from '../../../utils/metrics';
import { PAGE_NAME } from '../../../../configuration/constants';
import { NonNullablePrimaries } from './PrimarySection.types';

export const usePrimaryEnrichedData = (
    side: 'PRIMARY_LEFT' | 'PRIMARY_RIGHT',
    enrichPrimaryDataArgs: IPrimaryArgs,
    primaryData: LayoutPrimary,
    size: PrimarySize,
    enrichPrimaryData: PrimarySDKFunctions['enrichPrimaryData'],
    enrichFallbackPrimaryData: PrimarySDKFunctions['enrichFallbackPrimaryData'],
    ratio: PrimaryRatio,
    audiences: BuyerInsights['audiences']
): Optional<IPrimaryBasicData & IPrimaryEnrichment> => {
    const shouldExitEarly = side === 'PRIMARY_RIGHT' && ratiosEqual(ratio, XL);
    const basicPrimaryData: IPrimaryBasicData = useMemo(
        () => ({
            ...primaryData,
            size,
            audiences,
        }),
        [primaryData, size, audiences]
    );

    const enrichPrimaryDataArgsTotal: IPrimaryEnrichDataArgs = {
        ...basicPrimaryData,
        ...enrichPrimaryDataArgs,
    };

    // Muting the default react-query logs
    setLogger({
        log: noop,
        warn: noop,
        error: noop,
    });

    const query = useQuery(side, () => enrichPrimaryData?.(enrichPrimaryDataArgsTotal), {
        retry: false,
        refetchOnWindowFocus: false,
        enabled: !shouldExitEarly,
    });

    if (shouldExitEarly) {
        return undefined;
    }

    if (query.isLoading) {
        return undefined;
    }

    if (query.isSuccess) {
        return {
            ...basicPrimaryData,
            ...query.data,
        };
    }

    if (query.isError) {
        logger.warn(query.error as any, {
            primarySide: side,
            primaryType: basicPrimaryData.type,
            error: query.error,
            description: 'Failed to enrich data - using fallback',
        });

        metricsService.count(`primary.${primaryData.name}.load.failed`);

        return {
            ...basicPrimaryData,
            ...enrichFallbackPrimaryData?.(enrichPrimaryDataArgsTotal),
            pageName: PAGE_NAME,
        };
    }

    return undefined;
};

export const usePrimaryRatio = (originalRatio: PrimaryRatio, hasTwoPrimaries: boolean) => {
    const [ratio, setRatio] = useState(originalRatio);

    useEffect(() => {
        const handleResize = () => {
            if (hasTwoPrimaries) {
                setRatio(window.innerWidth < theme.numericBreakpoints.lg ? M_M : originalRatio);
            }
        };

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, [originalRatio, hasTwoPrimaries]);

    return { ratio };
};

export const calcGridColumnPlacement = (ratio: PrimaryRatio): ComponentProps<typeof GridItem>['columnEnd'] => {
    /*
        | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 |
        50-50 split: 1-6, 7-12 (M_M)
        | 1___________________6 | 7_____________________12 |
        30-70 split: 1-4, 5-12 (S_L)
        | 1___________4 | 5_____________________________12 |
        70-30 split: 1-8, 9-12 (L_S)
        | 1___________________________8 | 9_____________12 |
        single primary- full width (XL)
        | 1_____________________________________________12 |

        In the following return values, we add 1 to the end column because the end column is exclusive.
        */
    if (ratiosEqual(ratio, M_M)) {
        // 6 out of 12 columns - exactly half
        return '7';
    }

    if (ratiosEqual(ratio, XL)) {
        return '13';
    }

    return {
        default: '7', // in screen sizes smaller than lg, components split by 50%-50%
        lg: ratiosEqual(ratio, S_L) ? '5' : '9', // in screen sizes larger than lg, components split by 30%-70%
    };
};

export const isValidPrimarySection = (
    primariesToDisplay: TailoredHomepageInitialProps['primariesToDisplay'],
    ratio: PrimaryRatio
): primariesToDisplay is NonNullablePrimaries => {
    const numOfValidPrimaries = Object.values(primariesToDisplay).filter((primary) => primary !== null).length;
    const isXLRatio = ratiosEqual(ratio, XL);

    return numOfValidPrimaries === 2 || (numOfValidPrimaries === 1 && isXLRatio);
};
